import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FastTable } from "./Table/FastTable";
import { dateTimeInMsToString, timeInMsToStringUTC, dateTimeInMsToTimeString } from "./Services/ResultService";
import { useGlobalContext } from "./Layout/GlobalState";
import { CREDENTIALS_STORAGE_NAME } from "./Services/SigninService";

type ConnectionState = "connecting" | "open" | "error" | "closed";

type LiveResultsRow = {
  id: string;
  birthYear: number;
  eventType: string;
  clubOrTeam: string;
  competitionTag: string;
  disciplineName: string;
  firstname: string;
  lastname: string;
  startnumber: number;
  startTime: number;
  finishTime: number;
  //rawJson: string;
};

const MAX_ROWS = 300;

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function firstDefinedString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
    if (typeof value === "number") {
      return String(value);
    }
  }
  return "-";
}

function formatTimestamp(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value > 10_000_000_000 ? value : value * 1000;
    return dateTimeInMsToString(ms);
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return dateTimeInMsToString(parsed);
    }
    return value;
  }
  return "-";
}

function formatChipTime(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return timeInMsToStringUTC(value);
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const numeric = Number(value);
    return Number.isNaN(numeric) ? value : timeInMsToStringUTC(numeric);
  }
  return "-";
}

function mapEventToRow(eventType: string, payload: Record<string, unknown>): LiveResultsRow {
  const nestedResults = asRecord(payload.results);
  const nestedRead = asRecord(payload.read);
  const source = Object.keys(nestedResults).length > 0
    ? nestedResults
    : Object.keys(nestedRead).length > 0
    ? nestedRead
    : payload;

  const receivedAt = Date.now();
  const id = Number(source.id ?? payload.id) || Math.floor(receivedAt / 1000);
  const birthYear = Number(source.birthYear ?? payload.birthYear) || 0;
  const clubOrTeam = firstDefinedString(source.clubOrTeam, payload.clubOrTeam, payload.club, payload.team);
  const competitionTag = firstDefinedString(source.competitionTag, payload.competitionTag); 
  const disciplineName = firstDefinedString(source.disciplineName, payload.disciplineName, payload.discipline);
  const firstname = firstDefinedString(source.firstname, source.firstName, payload.firstname, payload.firstName);
  const lastname = firstDefinedString(source.lastname, source.lastName, payload.lastname, payload.lastName);
  const startnumber = Number(source.startnumber ?? source.startNumber ?? payload.startnumber ?? payload.startNumber) || 0;
  const startTime = Number(source.startTime ?? payload.startTime) || 0;
  const finishTime = Number(source.finishTime ?? payload.finishTime) || 0;


  return {
    eventType,
    id: String(id),
    birthYear,
    clubOrTeam,
    competitionTag,
    disciplineName,
    firstname,
    lastname,
    startnumber,
    startTime,
    finishTime,
    //rawJson: JSON.stringify(payload),
  };
}

type SseMessage = {
  eventType: string;
  data: string;
};

async function consumeSseStream(
  stream: ReadableStream<Uint8Array>,
  onMessage: (message: SseMessage) => void,
  signal: AbortSignal
) {
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");

  let buffer = "";
  let eventType = "message";
  let dataLines: Array<string> = [];

  const flushMessage = () => {
    if (dataLines.length === 0) {
      eventType = "message";
      return;
    }

    onMessage({
      eventType,
      data: dataLines.join("\n"),
    });

    eventType = "message";
    dataLines = [];
  };

  while (!signal.aborted) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const normalized = buffer.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const lines = normalized.split("\n");
    buffer = lines.pop() ?? "";

    for (const rawLine of lines) {
      const line = rawLine.trimEnd();

      if (line.length === 0) {
        flushMessage();
        continue;
      }

      if (line.startsWith(":")) {
        continue;
      }

      if (line.startsWith("event:")) {
        eventType = line.slice("event:".length).trim() || "message";
        continue;
      }

      if (line.startsWith("data:")) {
        dataLines.push(line.slice("data:".length).trimStart());
      }
    }
  }

  // Flush trailing event if stream ends without newline/new event delimiter.
  flushMessage();
}

export function LiveResults() {
  const globalContext = useGlobalContext();
  const [connectionState, setConnectionState] = useState<ConnectionState>("connecting");
  const [rows, setRows] = useState<Array<LiveResultsRow>>([]);
  const [errorText, setErrorText] = useState("");
  const [isStreaming, setIsStreaming] = useState(true);
  const [reconnectNonce, setReconnectNonce] = useState(0);

  useEffect(() => {
    if (!isStreaming) {
      setConnectionState("closed");
      return;
    }

    setConnectionState("connecting");
    setErrorText("");
    const abortController = new AbortController();

    const encodedCred = sessionStorage.getItem(CREDENTIALS_STORAGE_NAME);
    const headers: Record<string, string> = {
      Accept: "text/event-stream",
      "Cache-Control": "no-cache",
    };
    if (encodedCred) {
      headers.Authorization = `Basic ${encodedCred}`;
    }

    const addRow = (eventType: string, data: string) => {
      try {
        const parsed = JSON.parse(data) as Record<string, unknown>;
        const nextRow = mapEventToRow(eventType, parsed);
        if (nextRow.competitionTag !== globalContext.competition?.tag) {
          setErrorText(`Ignorerar händelse från tävling "${nextRow.competitionTag}" som inte matchar den valda tävlingen "${globalContext.competition?.tag}".`);
          return;
        }
        setRows((old) => [nextRow, ...old].slice(0, MAX_ROWS));
      } catch (error) {
        setErrorText("Meddelandet kunde inte tolkas som JSON.");
      }
    };

    const start = async () => {
      try {
        const response = await fetch("/api/results/live", {
          method: "GET",
          headers,
          credentials: "include",
          signal: abortController.signal,
        });

        if (!response.ok || !response.body) {
          setConnectionState("error");
          setErrorText(
            `Kunde inte läsa från /api/results/live (HTTP ${response.status}). Kontrollera endpoint och inloggning.`
          );
          return;
        }

        setConnectionState("open");
        setErrorText("");

        await consumeSseStream(
          response.body,
          (message) => {
            addRow(message.eventType, message.data);
          },
          abortController.signal
        );

        if (!abortController.signal.aborted) {
          setConnectionState("error");
          setErrorText("SSE-strömmen avslutades oväntat.");
        }
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }
        setConnectionState("error");
        setErrorText("Kunde inte läsa från /api/results/live. Kontrollera att SSE-endpointen är tillgänglig och accepterar aktuell inloggning.");
      }
    };

    start();

    return () => {
      abortController.abort();
    };
  }, [isStreaming, reconnectNonce]);

  const statusColorScheme = useMemo(() => {
    if (connectionState === "open") return "green";
    if (connectionState === "connecting") return "yellow";
    if (connectionState === "error") return "red";
    return "gray";
  }, [connectionState]);

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Text textAlign="left" fontSize="24" width="100%">
          Live resultat ({rows.length.toString()} händelser i minnet)
        </Text>
        <Text color="gray.600">
          Ström från /api/results/live för {globalContext.competition?.tag ?? "ingen vald tävling"}
        </Text>
      </Box>

      <HStack justify="space-between" align="center" wrap="wrap">
        <HStack>
          <Badge colorScheme={statusColorScheme} paddingX={3} paddingY={1} borderRadius="md">
            {connectionState}
          </Badge>
          {errorText.length > 0 && <Text color="red.600">{errorText}</Text>}
        </HStack>

        <HStack>
          <Text>Live uppdatering</Text>
          <Switch isChecked={isStreaming} onChange={(event) => setIsStreaming(event.currentTarget.checked)} />
          <Button onClick={() => setReconnectNonce((old) => old + 1)} colorScheme="blue">
            Anslut igen
          </Button>
          <Button onClick={() => setRows([])} variant="outline">
            Rensa lista
          </Button>
        </HStack>
      </HStack>

      <FastTable
        data={rows}
        pageHeader={`Live resultat ${new Date().toISOString().split("T")[0]}`}
        repeatHeaderRows={3}
        downloadFileName={`LiveResult_${new Date().toISOString().split("T")[0]}`}
        headers={[
          "Målgång",
          // "Event",
          "Startnr",
          "Namn",
          "Född",
          "Klubb/lag",
          "Tid",
          "Klass",
          // "Raw JSON",
        ]}
        rowFormat={"1.6fr 1.2fr 3fr 1fr 3fr 1.6fr 1fr"}
        toRowData={(row) => [
          dateTimeInMsToTimeString(row.finishTime > 0 ? row.finishTime : 0),
          //row.eventType,
          row.startnumber > 0 ? row.startnumber : "-",
          row.firstname !== "-" && row.lastname !== "-" ? `${row.firstname} ${row.lastname}` : "-",
          row.birthYear > 0 ? row.birthYear : "-",
          row.clubOrTeam,
          row.finishTime > 0 ? timeInMsToStringUTC((row.finishTime - row.startTime)) : "-",
          row.disciplineName,
          //row.rawJson,
        ]}
        height={500}
      />
    </VStack>
  );
}

export default LiveResults;